import React, { useEffect, useMemo, useState } from "react";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import PlatformAddNewUserDrawer from "../../components/platform/users/PlatformAddNewUserDrawer";
import PlatformUserProfileDrawer from "../../components/platform/users/PlatformUserProfileDrawer";
// import ImportUsersModal from "../../components/admin/users/ImportUsersModal";
import UserRole from "../../enums/Role";
import UserStatus from "../../enums/UserStatus";
import { useLocation } from "react-router-dom";
import { CHECK_ACTIVE_ADMINS } from "graphql/user";
import { useQuery } from "@apollo/client";
import AccountSearchAutoComplete, {
  type Account,
} from "components/common/AccountSearchAutoComplete";
import { GET_COMPANY } from "graphql/company";
import PlatformUsersTable from "components/platform/users/PlatfromUsersTable";
import Role from "../../enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { TablePage } from "@outpave/ds";

export type FormikUser = {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  employeeGroup: string;
  status: string;
  address: {
    id: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
  };
  location: string;
  assignedManager: string;
  userPassword: string;
  role: string;
  manager: string;
  sendOneTimePassword: boolean;
  cards: string[];
};

export type FilterData = {
  userName?: string;
  role?: UserRole[];
  status?: UserStatus[];
  manager?: string;
  activeCards?: undefined | number;
  searchTerm?: string;
  companyId?: string;
};

const PlatformUsers: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);
  const [selectedCompany, setSelectedCompany] = useState<Pick<
    Account,
    "id" | "businessLegalName"
  > | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const companyIdFromUrl = params.get("companyId");
    if (companyIdFromUrl) {
      return { id: companyIdFromUrl, businessLegalName: "" };
    }
    return { id: "showAll", businessLegalName: "Account: All" };
  });
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("companyId") ?? "";
  });
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
    undefined,
  );
  const [openAddNewUserDrawer, setOpenAddNewUserDrawer] =
    useState<boolean>(false);
  const [openUserProfileDrawer, setUserProfileDrawer] =
    useState<boolean>(false);
  // const [openImportUsers, setOpenImportUsers] = useState<boolean>(false);
  const [reactivateUser, setReactivateUser] = useState<boolean>(false);
  const [edit, setEdit] = useState<boolean>(false);

  const {
    data: isAtLeastOneAdmin,
    loading: checkingForAdmins,
    refetch: refetchActiveAdminsCheck,
  } = useQuery(CHECK_ACTIVE_ADMINS);

  const tabConfigurations = useMemo(
    () => [
      {
        label: t("Active/Pending"),
        status: [UserStatus.ACTIVE, UserStatus.PENDING],
      },
      {
        label: t("Show All"),
        status: [],
      },
    ],
    [t],
  );

  const filterData = useMemo<FilterData>(
    () => ({
      companyId: selectedCompany?.id,
      status: tabConfigurations[activeTab].status,
      searchTerm: searchTerm,
    }),
    [activeTab, searchTerm, selectedCompany, tabConfigurations],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const companyId = params.get("companyId");
    const id = params.get("id");
    const edit = params.get("edit") === "true";
    if (id) {
      handleOpenUserProfileDrawer(id, edit);
    } else if (companyId) {
      setSelectedCompanyId(companyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const isPlatform = useCheckUserRole(Role.PlatformAdmin);

  const [refreshUsers, setRefreshUsers] = useState(0);

  const { data: companyInfo } = useQuery(GET_COMPANY, {
    variables: { id: selectedCompanyId },
    skip: Boolean(!selectedCompanyId) || !isPlatform,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (companyInfo) {
      setSelectedCompany(companyInfo?.company);
    }
  }, [companyInfo]);

  const handleRefreshUsers = () => {
    setRefreshUsers((prev) => prev + 1);
    refetchActiveAdminsCheck();
  };

  const handleOpenAddNewUserDrawer = () => {
    setOpenAddNewUserDrawer(true);
  };

  const handleCloseOpenAddNewUserDrawer = () => {
    setOpenAddNewUserDrawer(false);
  };

  const handleOpenUserProfileDrawer = (
    userId: string,
    edit: boolean = false,
    reactivateUser: boolean = false,
  ) => {
    setSelectedUserId(userId);
    setUserProfileDrawer(true);
    setEdit(edit);
    setReactivateUser(reactivateUser);
  };

  const handleCloseOpenUserProfileDrawer = () => {
    setUserProfileDrawer(false);
    setSelectedUserId(undefined);
    setReactivateUser(false);
    setEdit(false);
  };

  return (
    <TablePage
      title={t("Users")}
      primaryAction={
        isAdminOrHigher && (
          <Button variant="primary-filled" onClick={handleOpenAddNewUserDrawer}>
            {t("Add User")}
          </Button>
        )
      }
      // TODO: restore when CSV import is implemented. Nothing reads the
      // chosen file yet, so the button opened a dialog that silently
      // discarded it. See the TODO on components/common/ImportCsvModal.
      // secondaryAction={
      //   isAdminOrHigher && (
      //     <Button
      //       variant="default-outlined"
      //       onClick={() => setOpenImportUsers(true)}
      //     >
      //       {t("Import")}
      //     </Button>
      //   )
      // }
      tabs={{
        items: tabConfigurations.map((tab, index) => ({
          id: String(index),
          label: t(tab.label),
        })),
        activeId: String(activeTab),
        onChange: (id) => setActiveTab(Number(id)),
      }}
      search={search}
      filters={
        <AccountSearchAutoComplete
          handleSelectAccount={(selectedAccount) => {
            setSelectedCompany(selectedAccount ?? null);
            setSelectedCompanyId(selectedAccount?.id ?? "");
          }}
          account={selectedCompany}
          width={320}
          aria-label={t("Account")}
          placeholder={t("All Accounts")}
          showAllOptions={true}
        />
      }
    >
      <PlatformUsersTable
        filterData={filterData}
        handleOpenUserProfileDrawer={handleOpenUserProfileDrawer}
        refreshUsers={refreshUsers}
        handleRefreshUsers={handleRefreshUsers}
      />

      <PlatformAddNewUserDrawer
        open={openAddNewUserDrawer}
        handleCloseDrawer={handleCloseOpenAddNewUserDrawer}
        handleCreatedUser={handleRefreshUsers}
        selectedCompany={selectedCompany}
      />

      <PlatformUserProfileDrawer
        selectedUserId={selectedUserId}
        open={openUserProfileDrawer}
        edit={edit}
        isReactivateUser={reactivateUser}
        handleCloseDrawer={handleCloseOpenUserProfileDrawer}
        handleUpdateUser={handleRefreshUsers}
      />

      {/* <ImportUsersModal
        open={openImportUsers}
        onClose={() => setOpenImportUsers(false)}
      /> */}
    </TablePage>
  );
};

export default PlatformUsers;
